import React, { useState } from "react";
import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import { MAX_COURSES_PER_MONTH, monthlyQuotaOf } from "../../../Utils/monthlyQuota";
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromNet } from "../../../Utils/VerifiedTrainerAmounts";
import { getActiveClientBrandConfig } from "../../../config/environment.config";
import { DV2 } from "../theme";
import { euros, hoursFromHalfHours, monthName, plural, studentFirstName } from "../format";
import { vocab } from "../vocabulary";
import { isRelationTeachrChild } from "../components/relationTeachr";
import { Card, InfoTip, KeyValue, TextLink, useDv2Theme } from "./Ui";

/**
 * Plafonds mensuels d'un élève, calculés comme dans l'ancien parcours
 * (SecondStep) : 8 cours par mois civil tous profs confondus, et un montant
 * plafonné à 1 980 € × (1 − frais) pour Teach'r et Déclarons, 1 980 € TTC pour
 * Verified Trainer.
 */
export const studentMonthFigures = (child: any, teachr: Teachr | null | undefined) => {
    // const child = client;
    const defaultFees = parseFloat((teachr as any)?.defaultFees ?? "0") || 0;
    const netMultiplier = 1 - defaultFees;
    const isVt = isVerifiedTrainer();
    // Comme ClientRadioList.toDisplayAmount : net VT converti en TTC, sinon la valeur de l'API telle quelle.
    const toDisplayAmount = (value: any) => (isVt ? vtTtcFromNet(value, teachr as any) : (parseFloat(value ?? "0") || 0));
    const declaredAmount = toDisplayAmount(child?.amountForTheMonth);
    const ceiling = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
    const quota = monthlyQuotaOf(child?.coursesThisRollingMonth, child?.coursesMonthByThisTeachr);
  
    return {
        quota,
        remainingCourses: quota ? quota.remaining : MAX_COURSES_PER_MONTH,
        mineCourses: quota ? quota.mine : 0,
        mineHalfHours: child?.duration ?? 0,
        /** Mois passé (API : durationlastmonth, amountlastmonth), pour la comparaison du tableau. */
        lastMonthHalfHours: child?.durationlastmonth ?? 0,
        /** Cours du mois passé, tous profs confondus (API : coursesLastMonth), null tant que l'API ne le renvoie pas. */
        lastMonthCourses: child?.coursesLastMonth ?? null,
        lastMonthAmount: toDisplayAmount(child?.amountlastmonth),
        declaredAmount,
        ceiling,
        remainingAmount: Math.max(0, ceiling - declaredAmount),
        isVt,
    };
};

/** Ce que recouvre le « montant déclaré » : hors frais de gestion (prix total TTC chez Verified Trainer). */
export const declaredAmountHelp = (): string =>
    isVerifiedTrainer()
        ? `Prix total TTC ${vocab.declared()} ce mois pour ${getActiveClientBrandConfig("", "cet ", "ce ")}, tous coachs confondus. C'est ce montant qui compte pour le plafond mensuel de l'avance immédiate.`
        : `Montant ${vocab.declared()} ce mois pour ${getActiveClientBrandConfig("", "cet ", "ce ")}, hors frais de gestion, tous prestataires confondus. C'est ce montant qui compte pour le plafond mensuel de l'avance immédiate.`;

interface Props {
    child: any;
    teachr: Teachr;
    /** Version mobile : une ligne repliée, « Détails » pour tout voir. */
    compact?: boolean;
}

/**
 * Encart « Ben en octobre » : cours restants, déjà déclarés par vous, montant
 * déclaré et plafond. Client en relation Teach'r : le montant déclaré du mois
 * comprend la part de la plateforme, il n'est pas montré au prof.
 */
const StudentMonthPanel = ({ child, teachr, compact = false }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const hideAmount = isRelationTeachrChild(child);
    const [open, setOpen] = useState(!compact);
    const f = studentMonthFigures(child, teachr);
    const ratio = f.ceiling > 0 ? Math.min(1, f.declaredAmount / f.ceiling) : 0;
    const title = `${studentFirstName(child)} en ${monthName(moment())}`;
    const remainingTone = f.remainingCourses === 0 ? DV2.danger : f.remainingCourses <= 2 ? DV2.warning : DV2.text;

    return (
        <Card tint style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily }} aria-label={title}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
                {compact && <TextLink label={open ? "Réduire" : "Détails"} onClick={() => setOpen((o) => !o)} size={12} />}
            </div>
            <KeyValue label={`${vocab.courses().charAt(0).toUpperCase()}${vocab.courses().slice(1)} restants`} value={<span style={{ color: remainingTone }}>{f.remainingCourses} sur {MAX_COURSES_PER_MONTH}</span>} />
            {(open || !compact) && (
                <>
                    <KeyValue label="Déjà déclarés par vous" value={`${plural(f.mineCourses, vocab.course(), vocab.courses())}, ${hoursFromHalfHours(f.mineHalfHours)}`} />
                    {!hideAmount && (
                        <>
                            <KeyValue label={<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>Montant déclaré <InfoTip text={declaredAmountHelp()} label="Ce que compte le montant déclaré" /></span>} value={`${euros(f.declaredAmount)} sur ${euros(f.ceiling)}`} />
                            <div aria-hidden style={{ height: 6, borderRadius: 3, background: "#fff", overflow: "hidden" }}>
                                <div style={{ width: `${ratio * 100}%`, height: "100%", background: ratio >= 1 ? DV2.danger : primary }} />
                            </div>
                        </>
                    )}
                    <div style={{ fontSize: 12, color: DV2.textSoft, lineHeight: 1.45 }}>
                        {hideAmount
                            ? <>L'avance immédiate couvre au maximum {MAX_COURSES_PER_MONTH} {vocab.courses()} {vocab.perClient()} et par mois.</>
                            : <>L'avance immédiate couvre au maximum {MAX_COURSES_PER_MONTH} {vocab.courses()} et {euros(f.ceiling)}{f.isVt ? " TTC" : ""} de montant total {vocab.declared()} {vocab.perClient()} et par mois.</>}
                    </div>
                </>
            )}
        </Card>
    );
};

export default StudentMonthPanel;
